import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Navbar } from "@/components/Navbar";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TenantGallery() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [assoc, setAssoc] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get(`/associations/${slug}`).then(({ data }) => setAssoc(data)).catch(() => {});
    api.get(`/associations/${slug}/gallery`).then(({ data }) => setPhotos(data)).catch(() => {});
  }, [slug]);

  if (!assoc) return <div className="min-h-screen bg-background" />;

  return (
    <div className="min-h-screen bg-background">
      <Navbar assoc={assoc} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button data-testid="gallery-back-btn" onClick={() => navigate(`/${slug}`)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> {t("back")}
        </button>
        <h1 className="font-heading font-extrabold text-3xl tracking-tight mb-8">{t("gallery")}</h1>
        {photos.length === 0 ? (
          <p className="text-sm text-muted-foreground py-16 text-center border border-dashed border-border rounded-lg">{t("no_photos")}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {photos.map((p) => (
              <button key={p.id} data-testid="gallery-photo" onClick={() => setSelected(p)} className="group relative rounded-lg overflow-hidden border border-border aspect-square">
                <img src={`${BACKEND}/api/gallery/${p.id}/image`} alt={p.caption || "photo"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                {p.caption && (
                  <span className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-2 text-left truncate">{p.caption}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl p-2">
          {selected && (
            <div>
              <img src={`${BACKEND}/api/gallery/${selected.id}/image`} alt={selected.caption || "photo"} className="w-full max-h-[75vh] object-contain rounded-md" />
              {selected.caption && <p className="text-sm text-muted-foreground px-2 py-3">{selected.caption}</p>}
              <p className="text-[10px] font-mono-data text-muted-foreground px-2 pb-2">
                {new Date(selected.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
